import { SelectFieldHandle, SelectFieldProps } from "./types";
import { forwardRef, useId, useRef } from "react";
import { useLayoutPropsUtil } from "../../../internal/hooks/useLayoutPropsUtil";
import { warnDeprecatedErrorUsage } from "../../../components/FieldMessage";
import { SelectFieldComboboxMode } from "./internal/SelectFieldComboboxMode";
import { SelectFieldSelectMode } from "./internal/SelectFieldSelectMode";
import {
  SelectFieldBaseInternalProps,
  SelectFieldComboboxInternalProps,
} from "../../../internal/types/selectFieldInternalTypes";
import { useSelectOrchestration } from "../../../internal/hooks/useSelectOrchestration";

export const SelectField = forwardRef<SelectFieldHandle, SelectFieldProps>(
  (propsWithLayout, ref) => {
    const { layoutStyles, componentProps } =
      useLayoutPropsUtil(propsWithLayout);

    const inputWrapperRef = useRef<HTMLDivElement>(null);

    const {
      id: idProp,
      value: selectedOption,
      initialLoad: initialLoadProp,
      displayMenuAs: displayMenuAsProp,
      lazy,
      loadOptions: loadOptionsProp,
      label,
      labelAiMark,
      hideLabel,
      placeholder,
      disableClearButton: disableClearButtonProp = false,
      debounceMs = 200,
      onSelectedOptionChange,
      cache,
      size,
      error,
      hint,
      description,
      errorAriaLive: _errorAriaLive,
      required,
      disabled,
      readOnly,
      prefix,
      suffix,
      warning,
      className,
      style,
      pinned,
      virtualize,
      disableSearch,
    } = componentProps;

    const groupToString =
      "groupToString" in componentProps
        ? componentProps.groupToString
        : undefined;
    const groupSorter =
      "groupSorter" in componentProps ? componentProps.groupSorter : undefined;

    const autoId = useId();
    const id = idProp ?? autoId;
    const helperUid = useId();

    warnDeprecatedErrorUsage("SelectField", error);

    const errorMessages =
      typeof error === "boolean" || error === undefined ? undefined : error;
    const hasHelperText = !!(hint || errorMessages || warning || description);
    const isDisabledOrReadOnly = (disabled || readOnly) ?? false;

    const {
      displayAs,
      initialLoad,
      initialLoadPerformed,
      setInitialLoadPerformed,
      options,
      loading,
      loadingMore,
      hasMore,
      loadOptions,
      loadMore,
    } = useSelectOrchestration({
      lazy,
      loadOptions: loadOptionsProp,
      cache,
      lazyOptions:
        "lazyOptions" in componentProps
          ? componentProps.lazyOptions
          : undefined,
      initialLoad: initialLoadProp,
      displayMenuAs: displayMenuAsProp,
      ref,
    });

    const disableClearButton =
      disableClearButtonProp || !selectedOption || isDisabledOrReadOnly;

    const baseProps: SelectFieldBaseInternalProps = {
      id,
      helperUid,
      selectedOption,
      onSelectedOptionChange,
      displayAs,
      isDisabledOrReadOnly,
      disabled,
      readOnly,
      required,
      placeholder,
      size,
      error,
      hasHelperText,
      disableClearButton,
      prefix,
      suffix,
      label,
      labelAiMark,
      hideLabel,
      hint,
      errorMessages,
      warning,
      description,
      className,
      style,
      layoutStyles,
      pinned,
      groupToString,
      groupSorter,
      virtualize,
      options,
      loading,
      loadingMore,
      hasMore,
      loadOptions,
      loadMore,
      initialLoad,
      initialLoadPerformed,
      setInitialLoadPerformed,
      inputWrapperRef,
    };

    if (disableSearch) {
      return <SelectFieldSelectMode {...baseProps} />;
    }

    const comboboxProps: SelectFieldComboboxInternalProps = {
      ...baseProps,
      debounceMs,
    };

    return <SelectFieldComboboxMode {...comboboxProps} />;
  },
);

SelectField.displayName = "SelectField";
